/**
 * ScrollableTabsBar — faixa horizontal rolável para linhas de abas
 *
 * Resolve o caso em que existem muitas abas (categorias do Kanban, pipelines do CRM,
 * categorias de Tarefas) e a linha fica fixa, impedindo o usuário de navegar.
 *
 * Recursos:
 *   - Rolagem horizontal real com barra fina sempre visível quando há excedente.
 *   - Rolagem por roda do mouse/touchpad (gesto vertical convertido em horizontal),
 *     usando listener nativo não-passivo (React registra `onWheel` como passivo).
 *   - Botões de seta exibidos somente quando há conteúdo cortado.
 *   - Rolagem automática para manter a aba ativa visível.
 *
 * CHANGELOG:
 *   RELEASE: 2026-08-28 v1.0.0 - Versão inicial.
 */
import * as React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';

interface ScrollableTabsBarProps {
  /** Conteúdo da faixa (normalmente uma <TabsList>) */
  children: React.ReactNode;
  /** Classes adicionais para o wrapper externo */
  className?: string;
  /** Classes adicionais para o container rolável */
  contentClassName?: string;
  /**
   * Chave da aba ativa. Quando muda, a faixa rola para deixar o elemento
   * com `[data-state="active"]` visível.
   */
  activeKey?: string | null;
}

/** Distância (px) rolada a cada clique nas setas */
const SCROLL_STEP = 200;

export function ScrollableTabsBar({
  children,
  className,
  contentClassName,
  activeKey,
}: ScrollableTabsBarProps) {
  const scrollerRef = React.useRef<HTMLDivElement | null>(null);
  const [canLeft, setCanLeft] = React.useState(false);
  const [canRight, setCanRight] = React.useState(false);

  /** Recalcula a visibilidade das setas conforme a posição do scroll */
  const updateArrows = React.useCallback(() => {
    const node = scrollerRef.current;
    if (!node) return;
    const maxScroll = node.scrollWidth - node.clientWidth;
    setCanLeft(node.scrollLeft > 1);
    setCanRight(node.scrollLeft < maxScroll - 1);
  }, []);

  // Observa mudanças de tamanho/conteúdo para atualizar as setas
  React.useEffect(() => {
    const node = scrollerRef.current;
    if (!node) return;

    updateArrows();

    const observer = new ResizeObserver(updateArrows);
    observer.observe(node);
    for (const child of Array.from(node.children)) observer.observe(child);

    node.addEventListener('scroll', updateArrows, { passive: true });
    return () => {
      observer.disconnect();
      node.removeEventListener('scroll', updateArrows);
    };
  }, [updateArrows, children]);

  // Converte gesto vertical do mouse/touchpad em rolagem horizontal
  React.useEffect(() => {
    const node = scrollerRef.current;
    if (!node) return;

    const handleWheel = (event: WheelEvent) => {
      const hasOverflow = node.scrollWidth > node.clientWidth + 1;
      if (!hasOverflow) return;

      // Normaliza delta em modo linha/página
      const factor = event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? node.clientWidth : 1;
      const delta = (Math.abs(event.deltaX) > Math.abs(event.deltaY) ? event.deltaX : event.deltaY) * factor;
      if (delta === 0) return;

      node.scrollLeft += delta;
      event.preventDefault();
      event.stopPropagation();
    };

    node.addEventListener('wheel', handleWheel, { passive: false });
    return () => node.removeEventListener('wheel', handleWheel);
  }, []);

  // Mantém a aba ativa visível ao trocar de aba
  React.useEffect(() => {
    const node = scrollerRef.current;
    if (!node) return;
    const active = node.querySelector<HTMLElement>('[data-state="active"]');
    active?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
  }, [activeKey]);

  /** Rola a faixa em uma direção */
  const scrollBy = (direction: -1 | 1) => {
    scrollerRef.current?.scrollBy({ left: direction * SCROLL_STEP, behavior: 'smooth' });
  };

  return (
    <div className={cn('relative flex min-w-0 items-center', className)}>
      {/* Seta esquerda — só aparece quando há conteúdo cortado à esquerda */}
      {canLeft && (
        <button
          type="button"
          aria-label="Rolar abas para a esquerda"
          onClick={() => scrollBy(-1)}
          className="absolute left-0 z-10 flex h-7 w-7 items-center justify-center rounded-full border bg-background/95 shadow-sm hover:bg-muted"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
      )}

      <div
        ref={scrollerRef}
        className={cn(
          // Barra fina sempre visível (utilitário definido no index.css do projeto)
          'flex min-w-0 flex-1 items-center gap-2 overflow-x-auto overflow-y-hidden pb-1',
          canLeft && 'pl-8',
          canRight && 'pr-8',
          contentClassName,
        )}
        style={{ scrollbarWidth: 'thin' }}
      >
        {children}
      </div>

      {/* Seta direita — indica que existem mais abas */}
      {canRight && (
        <button
          type="button"
          aria-label="Rolar abas para a direita"
          onClick={() => scrollBy(1)}
          className="absolute right-0 z-10 flex h-7 w-7 items-center justify-center rounded-full border bg-background/95 shadow-sm hover:bg-muted"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}
